import React, { useEffect, useState } from 'react';
import {
  createDispute,
  fetchDisputes,
  type Dispute as DisputeT,
} from '../api/finance';

export default function Disputes() {
  const [disputes, setDisputes] = useState<DisputeT[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState<{ type: string; msg: string } | null>(null);

  const [form, setForm] = useState({
    escrowId: '',
    filedBy: '',
    reason: '',
  });

  const load = async () => {
    try {
      const data = await fetchDisputes();
      setDisputes(data);
    } catch {
      /* keep current */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleFile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.escrowId || !form.filedBy || !form.reason) {
      setAlert({ type: 'error', msg: 'All fields are required.' });
      return;
    }
    setSubmitting(true);
    setAlert(null);
    try {
      await createDispute({
        escrowId: form.escrowId,
        filedBy: form.filedBy,
        reason: form.reason,
        status: 'open',
      });
      setAlert({ type: 'success', msg: 'Dispute filed successfully.' });
      setForm({ escrowId: '', filedBy: '', reason: '' });
      await load();
    } catch (e) {
      setAlert({ type: 'error', msg: String(e) });
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (s: string) => {
    const cls = s === 'open' ? 'warning' : s === 'resolved' ? 'success' : 'info';
    const icon = s === 'open' ? '⚠️' : s === 'resolved' ? '✅' : '❌';
    return <span className={`finance-badge ${cls}`}>{icon} {s}</span>;
  };

  /* ── Stats ── */
  const open = disputes.filter((d) => d.status === 'open').length;
  const resolved = disputes.filter((d) => d.status === 'resolved').length;
  const rejected = disputes.filter((d) => d.status === 'rejected').length;

  return (
    <div>
      <header className="finance-header">
        <h1>Dispute Resolution</h1>
        <p>File and manage disputes for contested escrow transactions</p>
      </header>

      {alert && (
        <div className={`finance-alert ${alert.type}`}>{alert.msg}</div>
      )}

      {/* ── KPI ── */}
      <div className="finance-grid" style={{ marginBottom: 24 }}>
        <div className="finance-card">
          <div className="finance-card-title">
            <span className="finance-icon">⚠️</span> Open
          </div>
          <div className="finance-stat-value negative">{open}</div>
        </div>
        <div className="finance-card">
          <div className="finance-card-title">
            <span className="finance-icon">✅</span> Resolved
          </div>
          <div className="finance-stat-value positive">{resolved}</div>
        </div>
        <div className="finance-card">
          <div className="finance-card-title">
            <span className="finance-icon">❌</span> Rejected
          </div>
          <div className="finance-stat-value">{rejected}</div>
        </div>
      </div>

      {/* ── File Dispute ── */}
      <div className="finance-card" style={{ marginBottom: 24 }}>
        <div className="finance-card-header">
          <span className="finance-card-title">
            <span className="finance-icon">🛡️</span>
            File a Dispute
          </span>
        </div>
        <form onSubmit={handleFile} className="finance-form">
          <div className="finance-form-row">
            <div className="finance-form-group">
              <label className="finance-label">Escrow ID</label>
              <input
                className="finance-input"
                placeholder="escrow-001"
                value={form.escrowId}
                onChange={(e) => setForm({ ...form, escrowId: e.target.value })}
              />
            </div>
            <div className="finance-form-group">
              <label className="finance-label">Filed By (Wallet / Agent ID)</label>
              <input
                className="finance-input"
                placeholder="0x1234… or agent-beta"
                value={form.filedBy}
                onChange={(e) => setForm({ ...form, filedBy: e.target.value })}
              />
            </div>
          </div>
          <div className="finance-form-group">
            <label className="finance-label">Reason</label>
            <textarea
              className="finance-input finance-textarea"
              placeholder="Describe the dispute reason in detail…"
              rows={4}
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            />
          </div>
          <button className="finance-button" type="submit" disabled={submitting}>
            {submitting ? 'Filing…' : '🛡️ File Dispute'}
          </button>
        </form>
      </div>

      {/* ── Dispute List ── */}
      <div className="finance-card">
        <div className="finance-card-header">
          <span className="finance-card-title">
            <span className="finance-icon">📋</span>
            All Disputes
          </span>
          <span className="finance-badge warning">{disputes.length}</span>
        </div>
        {loading ? (
          <div className="finance-loading">Loading…</div>
        ) : disputes.length === 0 ? (
          <div className="finance-empty">No disputes filed. The system is running smoothly.</div>
        ) : (
          <div className="finance-table-wrap">
            <table className="finance-table">
              <thead>
                <tr>
                  <th>Escrow</th>
                  <th>Filed By</th>
                  <th>Reason</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {disputes.map((d, i) => (
                  <tr key={d.id || i}>
                    <td className="mono">{d.escrowId}</td>
                    <td className="mono">{d.filedBy}</td>
                    <td>{d.reason}</td>
                    <td>{statusBadge(d.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Dispute Resolution Process ── */}
      <div className="finance-card" style={{ marginTop: 24 }}>
        <div className="finance-card-header">
          <span className="finance-card-title">
            <span className="finance-icon">⚖️</span>
            Resolution Process
          </span>
        </div>
        <div className="finance-timeline">
          {[
            { step: '1', label: 'Dispute Filed', desc: 'Party submits dispute with evidence and reason' },
            { step: '2', label: 'Review Period', desc: '48h review window — both parties can submit evidence' },
            { step: '3', label: 'Arbitration', desc: 'Verifier panel votes on dispute outcome' },
            { step: '4', label: 'Resolution', desc: 'Funds redistributed based on verdict' },
          ].map((t, i) => (
            <div className="finance-timeline-step" key={i}>
              <div className="finance-timeline-marker">{t.step}</div>
              <div>
                <strong>{t.label}</strong>
                <p className="finance-stat-label">{t.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
